/**
 * Session-stable colour assignment for failure modes (design doc 015 §6).
 *
 * The old scheme hashed each `failure_mode_id` independently into the palette,
 * so with only a handful of modes two of them collided on the same colour more
 * often than not (birthday problem). Instead we assign palette *slots* to the
 * modes that are actually drawn, coordinating so co-visible modes stay distinct.
 *
 * The requirement is stability *within a viewing session* — a mode keeps its
 * colour while you pan the map, scrub the time slider, flip daily/weekly, and
 * across the scatter/legend/detail panels — but it may change on a hard refresh.
 * That's exactly what a sticky, in-memory assignment gives (the map lives in a
 * ref that resets on unmount). It deliberately does NOT depend on the mode id's
 * hash, its `first_seen_at`, or which window is in view.
 */

/**
 * Assign palette slot indices to the currently-visible modes, mutating the
 * sticky `slots` map in place:
 *
 *   - modes already in `slots` keep their slot — so panning, scrubbing, and
 *     toggling one mode never recolour the others;
 *   - each newly-visible, unassigned mode (walked in `orderedIds` order) takes
 *     the slot least used among the visible modes assigned so far, so the drawn
 *     modes stay distinct until there are more of them than palette colours.
 *
 * Only visible modes are assigned here; a mode that has never been drawn gets no
 * slot (the caller supplies a deterministic fallback for its legend swatch, and
 * it receives a real slot the first time it becomes visible). `orderedIds` is the
 * API's stable mint order, so ties resolve deterministically within a session.
 */
export function assignVisibleSlots(
  orderedIds: readonly string[],
  visibleIds: ReadonlySet<string>,
  slots: Map<string, number>,
  paletteSize: number,
): void {
  // How many currently-visible modes already sit on each slot.
  const usage = new Array<number>(paletteSize).fill(0);
  for (const [id, slot] of slots) {
    if (visibleIds.has(id) && slot >= 0 && slot < paletteSize) usage[slot]++;
  }
  for (const id of orderedIds) {
    if (!visibleIds.has(id) || slots.has(id)) continue;
    let best = 0;
    for (let s = 1; s < paletteSize; s++) {
      if (usage[s] < usage[best]) best = s;
    }
    slots.set(id, best);
    usage[best]++;
  }
}
